"use client";

import type { ReactNode } from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { IconType } from "react-icons";

import { cn } from "@/utils/lib";

import { HorizontalScrollRail } from "./horizontal-scroll-rail";
import { StickyStack } from "./sticky-stack";

export type SurfaceSelectorRowItem<Value> = {
  icon: IconType;
  key: string;
  label: string;
  value: Value;
};

type SurfaceSelectorRowProps<Value> = {
  activeItemKey: string;
  actions?: ReactNode;
  className?: string;
  items: SurfaceSelectorRowItem<Value>[];
  onSelectItem: (value: Value) => void;
};

export const SurfaceSelectorRow = <Value,>({
  activeItemKey,
  actions,
  className,
  items,
  onSelectItem,
}: SurfaceSelectorRowProps<Value>) => {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const itemButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [buttonsReady, setButtonsReady] = useState(false);
  const initialScrollSettledRef = useRef(false);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const railRoot = viewport?.parentElement ?? null;
    const selectedButton = itemButtonRefs.current[activeItemKey] ?? null;

    if (!viewport || !selectedButton) {
      return;
    }

    const getOverlayInset = (direction: "left" | "right") => {
      const button = railRoot?.querySelector(
        `[data-horizontal-scroll-rail-button="${direction}"]`,
      );

      if (!(button instanceof HTMLElement)) {
        return 12;
      }

      const style = window.getComputedStyle(button);

      if (style.display === "none" || style.visibility === "hidden") {
        return 12;
      }

      return button.offsetWidth + 8;
    };

    const leftInset = getOverlayInset("left");
    const rightInset = getOverlayInset("right");
    const targetLeft = selectedButton.offsetLeft;
    const targetRight = targetLeft + selectedButton.offsetWidth;
    const visibleLeft = viewport.scrollLeft;
    const visibleRight = visibleLeft + viewport.clientWidth;

    if (targetLeft < visibleLeft + leftInset) {
      viewport.scrollLeft = Math.max(targetLeft - leftInset, 0);
      return;
    }

    if (targetRight > visibleRight - rightInset) {
      viewport.scrollLeft = targetRight - viewport.clientWidth + rightInset;
    }
  }, [activeItemKey, buttonsReady, items]);

  useEffect(() => {
    if (initialScrollSettledRef.current) {
      return;
    }

    const rafId = window.requestAnimationFrame(() => {
      initialScrollSettledRef.current = true;
      setButtonsReady(true);
    });

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <StickyStack
      className={cn(
        `
          unforce-radius z-20 flex items-center gap-2 border-y border-border
          bg-background/95 p-2 backdrop-blur-md
        `,
        className,
      )}
    >
      <div className="min-w-0 flex-1">
        <HorizontalScrollRail
          className="w-full"
          contentClassName="gap-1"
          scrollStep={220}
          showButtons={buttonsReady}
          viewportRef={viewportRef}
        >
          {items.map((item) => {
            const Icon = item.icon;
            const selected = item.key === activeItemKey;

            return (
              <button
                key={item.key}
                ref={(element) => {
                  itemButtonRefs.current[item.key] = element;
                }}
                type="button"
                aria-current={selected ? "page" : undefined}
                onClick={() => {
                  onSelectItem(item.value);
                }}
                className={cn(
                  `
                    inline-flex shrink-0 items-center gap-2 rounded-full border
                    p-2 text-sm transition-colors
                  `,
                  selected
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-transparent bg-background text-muted hover:border-border hover:text-foreground",
                )}
              >
                <Icon className="size-4 shrink-0" />
                <span className="max-w-[140px] truncate">{item.label}</span>
              </button>
            );
          })}
        </HorizontalScrollRail>
      </div>

      {actions && (
        <div className="flex shrink-0 items-center gap-2">{actions}</div>
      )}
    </StickyStack>
  );
};
